/**
 * Müşteri kazanımı ekranlarının metinleri (v3.1 S9): Aday merkezi, Gelen aday uçları, Dağıtım kuralları, Bekleyen dağıtım ve
 * istisnalar, Kaynak ve kampanyalar, Kampanya raporu ve kayıt panelleri ("Kaynak", "Neden bu temsilci?"). Bütün görünen metin
 * Türkçedir.
 */
import type {
  DistributionErrorCode,
  DistributionIneligible,
  DistributionOutcome,
  LeadEventDecision,
  LeadConnectorKind,
  LeadEventErrorCode,
  LeadEventFilter,
  LeadEventStatus,
} from '@castinflow/shared';
import {
  DISTRIBUTION_ERROR_LABELS,
  DISTRIBUTION_INELIGIBLE_LABELS,
  DISTRIBUTION_OUTCOME_LABELS,
  LEAD_EVENT_DECISION_LABELS,
  LEAD_EVENT_ERROR_LABELS,
  LEAD_EVENT_FILTER_LABELS,
  LEAD_EVENT_STATUS_LABELS,
} from '@castinflow/shared';

const n = (x: number) => x.toLocaleString('tr-TR');

export const acquisitionNav = {
  leadCenter: 'Aday merkezi',
  distributionQueue: 'Bekleyen dağıtım',
  campaignReport: 'Kampanya raporu',
  endpoints: 'Gelen aday uçları',
  distributionRules: 'Dağıtım kuralları',
  acquisition: 'Kaynak ve kampanyalar',
};

export const acqText = {
  failed: 'İşlem tamamlanamadı. Yeniden deneyin.',
  loadFailed: 'Bilgiler yüklenemedi. Sayfayı yenileyip yeniden deneyin.',
  retry: 'Yeniden dene',
  more: 'Daha fazla göster',
  cancel: 'Vazgeç',
  save: 'Kaydet',
  close: 'Kapat',
  never: 'Hiç',
  none: '—',
  readOnlyAdmin: 'Bu ekranı yalnız çalışma alanı yöneticileri yönetebilir.',
  copy: 'Kopyala',
  copied: 'Kopyalandı',
  active: 'Etkin',
  passive: 'Pasif',
  leadsCrumb: 'Müşteri adayları',
  settingsCrumb: 'Yönetim',

  // --- Aday merkezi ---
  center: {
    title: 'Aday merkezi',
    description:
      'Bütün kaynaklardan gelen başvurular önce bu havuza düşer. Hatalı ve başarısız olaylar burada görünür; başarısız olanlar yeniden işlenebilir. Kişisel veriler maskelidir.',
    noAccess:
      'Aday merkezini görmek için "aday olaylarını yönetme" yetkisi gerekir. Yöneticinizden isteyin.',
    filtersLabel: 'Durum süzgeci',
    filter: LEAD_EVENT_FILTER_LABELS satisfies Record<LeadEventFilter, string>,
    status: LEAD_EVENT_STATUS_LABELS satisfies Record<LeadEventStatus, string>,
    decision: LEAD_EVENT_DECISION_LABELS satisfies Record<LeadEventDecision, string>,
    error: LEAD_EVENT_ERROR_LABELS satisfies Record<LeadEventErrorCode, string>,
    /** Olayın kaynağı (bağlayıcı türü; v3.1 S13) */
    connector: {
      webhook: 'Genel web kancası',
      google_lead_form: 'Google Ads potansiyel müşteri formu',
      meta_lead_ads: 'Meta potansiyel müşteri reklamları',
      whatsapp_ctwa: 'WhatsApp’a yönlendiren reklam (iş mesajlaşma konuşması)',
    } satisfies Record<LeadConnectorKind, string>,
    empty: 'Bu süzgeçte olay yok.',
    emptyHint: 'Gelen aday uçlarından ilk başvuru geldiğinde burada görünür.',
    endpointFilter: 'Uç',
    allEndpoints: 'Bütün uçlar',
    received: 'Alındı',
    endpoint: 'Uç',
    lead: 'Aday',
    attempts: (a: number) => `${n(a)} deneme`,
    duplicates: (d: number) => `${n(d)} kez tekrar geldi`,
    test: 'Deneme olayı',
    nextAttempt: (when: string) => `Sonraki otomatik deneme: ${when}`,
    select: (id: string) => `${id} olayını seç`,
    reprocess: (k: number) => `Seçilenleri yeniden işle (${n(k)})`,
    reprocessDone: (ok: number, fail: number) =>
      fail === 0
        ? `${n(ok)} olay işlendi`
        : `${n(ok)} olay işlendi, ${n(fail)} olay yine başarısız`,
    details: 'Ayrıntı',
    hideDetails: 'Ayrıntıyı gizle',
    masked: 'Maskeli görünüm',
    maskedName: 'Ad',
    maskedCompany: 'Şirket',
    maskedEmail: 'E-posta',
    maskedPhone: 'Telefon',
    maskedMessageTerm: 'İleti',
    maskedMessage: (len: number) => `${n(len)} karakterlik ileti`,
    utm: 'Bağlantı etiketleri',
    clickIds: 'Tıklama kimliği türleri',
    landing: 'Açılış yolu',
    referrer: 'Yönlendiren',
    platform: 'Platform alanları',
    errors: 'Doğrulama hataları',
    rawGone: 'Ham yük saklama süresi dolduğu için silindi.',
    rawShow: 'Ham yükü göster',
    rawTitle: 'Ham yük',
    rawWarning:
      'Ham yük kişisel veri içerir. Görüntüleme denetim kayıtlarına yazılır. Devam etmek istiyor musunuz?',
    rawConfirm: 'Göster ve kaydet',
    purgeAfter: (when: string) => `Ham yük ${when} tarihinde silinecek`,
    counts: 'Durum sayıları',
  },

  // --- Gelen aday uçları ---
  endpoints: {
    title: 'Gelen aday uçları',
    description:
      'Web sitesi, açılış sayfası ya da reklam platformu başvuruları bu uçlara gönderir. Her ucun anahtarı yalnız aday göndermeye yetkilidir. Anahtar yalnız oluşturma ve döndürme anında bir kez gösterilir.',
    empty: 'Henüz uç yok.',
    create: 'Yeni uç',
    createTitle: 'Yeni gelen aday ucu',
    kind: 'Uç türü',
    kindHint: {
      webhook:
        'Gövde JSON; her istek X-Castinflow-Signature başlığında HMAC-SHA256 imzası (t=<unix>,v1=<onaltılık>) taşır. Zaman farkı en çok 5 dakika.',
      google_lead_form:
        'Google Ads potansiyel müşteri formu öğesinin "Web kancası" alanına adresi, "Anahtar" alanına anahtarı yapıştırın. Deneme verisi kaydedilir, aday oluşturulmaz.',
    },
    name: 'Ad',
    source: 'Aday kaynağı',
    sourceHint: 'Bu uçtan gelen adaylar bu kaynakla oluşur (bağlantı etiketi eşlemesi yoksa).',
    defaultOwner: 'Varsayılan sorumlu',
    defaultOwnerHint:
      'Hiçbir dağıtım kuralı tutmazsa aday bu kullanıcıya atanır; boşsa aday istisnalar kuyruğuna düşer.',
    url: 'Gönderim adresi',
    secretTitle: 'Uç anahtarı',
    secretOnce:
      'Bu anahtar yalnız şimdi gösterilir. Kopyalayıp güvenli bir yerde saklayın; kaybederseniz anahtarı döndürmeniz gerekir.',
    secretVersion: (v: number) => `Anahtar sürümü ${n(v)}`,
    previous: (v: number, until: string) =>
      `Önceki anahtar (sürüm ${n(v)}) ${until} tarihine kadar geçerli`,
    lastEvent: 'Son olay',
    metrics: 'Son 7 gün',
    metric: {
      accepted: 'kabul',
      duplicate: 'tekrar',
      rejected: 'reddedildi',
      bad_signature: 'imzasız / yanlış imza',
      suspended: 'askı nedeniyle reddedildi',
      rate_limited: 'hız sınırı',
      invalid: 'çözümlenemedi',
    } as Record<string, string>,
    edit: 'Düzenle',
    editTitle: 'Ucu düzenle',
    isActive: 'Uç etkin',
    rotate: 'Anahtarı döndür',
    rotateTitle: 'Anahtarı döndür',
    rotateHint:
      'Yeni anahtar üretilir. Geçiş süresince önceki anahtar da kabul edilir; süre sonunda imha edilir.',
    overlap: 'Geçiş süresi',
    overlapOption: (h: number) =>
      h === 0 ? 'Geçişsiz (önceki anahtar hemen geçersiz)' : `${n(h)} saat`,
    expirePrevious: 'Önceki anahtarı şimdi sonlandır',
    saved: 'Uç kaydedildi',
    created: 'Uç oluşturuldu',
    rotated: 'Anahtar döndürüldü',
    expired: 'Önceki anahtar sonlandırıldı',
  },

  // --- Kaynak ve kampanyalar ---
  acquisition: {
    title: 'Kaynak ve kampanyalar',
    description:
      'Kampanya sözlüğü, bağlantı etiketi eşlemeleri ve kaynak izleme tercihleri. Kaynağı bilinmeyen başvuru tahminle bir kampanyaya bağlanmaz.',
    tabsLabel: 'Kaynak ve kampanya bölümleri',
    tabs: { campaigns: 'Kampanyalar', aliases: 'Eşlemeler', preferences: 'Tercihler' },
    campaigns: {
      empty: 'Henüz kampanya yok. Reklam formundan gelen kampanyalar burada kendiliğinden görünür.',
      create: 'Kampanya ekle',
      createTitle: 'Yeni kampanya',
      platform: 'Platform',
      level: 'Düzey',
      parent: 'Üst düzey',
      externalId: 'Platformdaki kimlik',
      externalIdHint: 'İsteğe bağlı. Reklam platformunun kampanya/grup/reklam kimliği.',
      name: 'Ad',
      archive: 'Arşivle',
      unarchive: 'Etkinleştir',
      archived: 'Arşivde',
      waCode: 'WhatsApp referans kodu',
      waGenerate: 'Kod üret',
      waRegenerate: 'Yeni kod üret',
      waHint:
        'Kod, WhatsApp bağlantısının önceden doldurulmuş metnine eklenir. Gelen ilk iletide kod bulunursa kaynak "ilişkilendirildi" olur; kullanıcı kodu silerse kaynak bilinmez.',
      waLink: 'WhatsApp bağlantısı',
      waNoNumber: 'Bağlantı için Tercihler bölümünde WhatsApp numarası girin.',
      saved: 'Kampanya kaydedildi',
    },
    aliases: {
      empty: 'Henüz eşleme yok.',
      field: 'Alan',
      raw: 'Gelen değer',
      rawHint: 'Büyük/küçük harf ve boşluk farkı yok sayılır.',
      target: 'Hedef',
      targetText: 'Birleştirilecek yazım',
      add: 'Eşleme ekle',
      remove: 'Kaldır',
      removeLabel: (v: string) => `"${v}" eşlemesini kaldır`,
      added: 'Eşleme eklendi',
      removed: 'Eşleme kaldırıldı',
    },
    prefs: {
      clickId: 'Tıklama kimliği toplama',
      clickIdHint:
        'Açıkken reklam tıklama kimlikleri (gclid, fbclid …) yalnız şifreli saklanır; ekranda hiçbir zaman gösterilmez. Varsayılan kapalıdır.',
      clickIdOn: 'Tıklama kimliklerini topla',
      notice:
        'Kişilere tıklama kimliği toplandığını bildiren aydınlatma metni ve gerekiyorsa açık rıza metni hukuki olarak onaylandı.',
      noticeHint:
        'Castinflow aydınlatma ya da rıza metni üretmez. Bu beyan tarihiyle birlikte denetim kayıtlarına yazılır.',
      noticeDate: 'Onay tarihi',
      noticeRequired: 'Açmak için onay beyanını işaretleyin ve tarih girin.',
      confirmedAt: (when: string) => `Beyan: ${when}`,
      duplicate: 'Aynı kişi yeniden başvurursa',
      qualified: 'Nitelikli sayılan aday durumları',
      qualifiedHint: 'Kampanya raporundaki "nitelikli" sütunu bu durumlardaki adayları sayar.',
      whatsapp: 'WhatsApp numarası',
      whatsappHint:
        'Uluslararası biçimde, ör. +905321234567. Kampanya bağlantıları bu numaraya gider.',
      saved: 'Tercihler kaydedildi',
    },
  },

  // --- Dağıtım kuralları ---
  rules: {
    title: 'Dağıtım kuralları',
    description:
      'Yeni adaylar, koşulu tutan ilk kuralın temsilcilerine ağırlıklarına göre dağıtılır. Mesai dışındaki, durdurulmuş ya da sınırı dolmuş temsilci atlanır; uygun temsilci yoksa aday bekleyen dağıtım kuyruğuna düşer.',
    empty: 'Henüz dağıtım kuralı yok. Kural yokken adaylar ucun varsayılan sorumlusuna atanır.',
    create: 'Kural ekle',
    createTitle: 'Yeni dağıtım kuralı',
    editTitle: 'Kuralı düzenle',
    name: 'Ad',
    priority: 'Öncelik',
    priorityHint: 'Küçük sayı önce denenir.',
    isActive: 'Kural etkin',
    condition: 'Koşul',
    conditionAll: 'Her aday',
    conditionSet: 'Koşul tanımlı',
    conditionEdit: 'Koşulu düzenle',
    conditionClear: 'Koşulu kaldır',
    window: 'Sayaç penceresi',
    useCalendar: 'Yalnız çalışma takvimine göre mesai saatlerinde dağıt',
    owner: 'Kuralın sorumlu yöneticisi',
    ownerHint: 'Kural bu kullanıcının yetkisiyle değerlendirilir; yönetici yetkisi taşımalıdır.',
    ownerProblem: {
      inactive: 'Kuralın sorumlu yöneticisi etkin değil.',
      forbidden: 'Kuralın sorumlu yöneticisinin yönetim yetkisi yok.',
    },
    ownerProblemActive:
      'Bu kural etkinken uyan adaylar istisnaya düşer. Düzenleyip yönetici yetkisi olan bir sorumlu seçin ya da kuralı durdurun.',
    members: 'Temsilciler',
    member: 'Temsilci',
    addMember: 'Temsilci ekle',
    removeMember: 'Temsilciyi çıkar',
    weight: 'Ağırlık (1–100)',
    paused: 'Durdur',
    dailyCap: 'Günlük üst sınır',
    capacity: 'Kapasite (açık aday)',
    schedule: 'Kendi çalışma saatleri',
    scheduleHint:
      'Boş bırakılırsa yalnız kuralın takvimi uygulanır. Saatler çalışma alanı saat dilimindedir.',
    from: 'Başlangıç',
    to: 'Bitiş',
    assigned: (k: number) => `${n(k)} atama`,
    version: (v: number) => `Sürüm ${n(v)}`,
    reset: 'Sayaçları yeniden başlat',
    resetTitle: 'Sayaçları yeniden başlat',
    resetHint: 'Yeni bir sayaç penceresi başlar; eski sayaçlar silinmez. Gerekçe denetime yazılır.',
    reason: 'Gerekçe',
    reasonShort: 'Gerekçe en az 5 karakter olmalı',
    saved: 'Kural kaydedildi',
    resetDone: 'Sayaçlar yeniden başlatıldı',
    conflict: 'Kural siz düzenlerken değişti. Sayfayı yenileyip yeniden deneyin.',
    weekdays: ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'],
    weekdayLabel: (d: string) => `${d} çalışır`,
  },

  // --- Kuyruklar ---
  queue: {
    title: 'Bekleyen dağıtım ve istisnalar',
    description:
      'Sorumlusu atanmamış adaylar, neden beklediği ve çözüm işlemiyle. Yalnız görebildiğiniz adaylar listelenir ve sayılır.',
    rulesLink: 'Dağıtım kuralları',
    tabsLabel: 'Kuyruk',
    pending: (k: number) => `Bekleyen dağıtım (${n(k)})`,
    exception: (k: number) => `İstisnalar (${n(k)})`,
    pendingIntro:
      'Kural eşleşti ama şu an uygun temsilci yok. Temsilci uygun olduğunda aday kendiliğinden atanır; beklemek istemiyorsanız elle atayın.',
    exceptionIntro:
      'Kural hatası ya da uyan kural yok: bu adaylar kendiliğinden atanmaz. Kuralı düzeltip yeniden dağıtın ya da elle atayın.',
    emptyPending: 'Görebildiğiniz adaylar arasında temsilci bekleyen yok.',
    emptyException: 'Görebildiğiniz adaylar arasında çözüm bekleyen istisna yok.',
    emptyHint: 'Yeni adaylar kurallara göre dağıtılır; sorun çıkarsa burada nedeniyle görünür.',
    pendingPlain: 'Bekleyen dağıtım',
    exceptionPlain: 'İstisnalar',
    owner: 'Sorumlu',
    unassigned: 'Atanmadı',
    rule: 'Kural',
    noRule: 'Kural yok',
    source: 'Kaynak',
    waiting: 'Bekliyor',
    waitingFor: (d: string) => `${d} bekliyor`,
    queuedAt: (d: string) => `Kuyruğa giriş: ${d}`,
    next: 'Sonraki adım',
    reasonTitle: {
      pending: 'Uygun temsilci yok',
      no_rule: 'Uyan kural yok',
      error: 'Kural hatası',
    },
    noMembers: 'Kuralda değerlendirilecek temsilci yok.',
    noRuleDetail: 'Bu adaya uyan etkin dağıtım kuralı ve kaynağın varsayılan sorumlusu yok.',
    nextStep: {
      pending: 'Temsilci uygun olunca kendiliğinden atanır (dakikada bir denenir).',
      no_rule: 'Kendiliğinden atanmaz: kural ekleyin ya da elle atayın.',
      error: 'Kendiliğinden atanmaz: kuralı düzeltip yeniden dağıtın ya da elle atayın.',
    },
    take: 'Kendime al',
    takeLabel: (t: string) => `${t} adayını kendinize alın`,
    taken: 'Aday size atandı',
    assign: 'Elle ata',
    assignLabel: (t: string) => `${t} adayını bir kullanıcıya atayın`,
    assignTitle: 'Adayı elle ata',
    assignHint: (t: string) => `${t} seçtiğiniz kullanıcıya atanır ve kuyruktan çıkar.`,
    assignTo: 'Atanacak kullanıcı',
    assignConfirm: 'Ata',
    assignPick: 'Bir kullanıcı seçin',
    assigned: 'Aday atandı',
    retryRule: 'Yeniden dağıt',
    retried: 'Dağıtım yeniden denendi',
    fixRule: 'Kuralı düzelt',
    addRule: 'Kural ekle',
    stale: 'Aday bu arada atanmış ya da kuyruktan çıkmış; liste yenilendi.',
    minutes: (k: number) => `${n(k)} dk`,
    hours: (h: number, m: number) => (m === 0 ? `${n(h)} sa` : `${n(h)} sa ${n(m)} dk`),
    days: (k: number) => `${n(k)} gün`,
    lessThanMinute: '1 dakikadan az',
  },

  // --- Kayıt panelleri ---
  record: {
    tab: 'Kaynak ve dağıtım',
    sourceTitle: 'Kaynak ve temaslar',
    snapshot: 'Değerler adayın dönüşüm anındaki görüntüsüdür; sonradan değişmez.',
    first: 'İlk temas',
    last: 'Son temas',
    none: 'Temas kaydı yok.',
    history: 'Bütün temaslar',
    certainty: 'Kesinlik',
    campaign: 'Kampanya',
    noCampaign: 'Kampanya bilinmiyor',
    notCertain: 'kampanya kesin değil',
    channel: 'Giriş yolu',
    source: 'Kaynak',
    duplicate: (k: number) => `Olası mükerrer: eşleştirme kuyruğunda ${n(k)} bekleyen çift`,
    whyTitle: 'Neden bu temsilci?',
    whyEmpty: 'Bu aday için dağıtım kararı yok (elle oluşturulmuş ya da kural öncesi).',
    inQueue: { pending: 'Bekleyen dağıtımda', exception: 'İstisnalarda' },
    outcome: DISTRIBUTION_OUTCOME_LABELS satisfies Record<DistributionOutcome, string>,
    errorCode: DISTRIBUTION_ERROR_LABELS satisfies Record<DistributionErrorCode, string>,
    ineligible: DISTRIBUTION_INELIGIBLE_LABELS satisfies Record<DistributionIneligible, string>,
    candidates: 'Aday temsilciler',
    chosen: 'Seçilen',
    eligible: 'Uygun',
    weightCount: (w: number, c: number) => `ağırlık ${n(w)} · ${n(c)} atama`,
    ruleVersion: (name: string, v: number | null) =>
      v === null ? name : `${name} (sürüm ${n(v)})`,
    decidedBy: (who: string) => `Karar veren: ${who}`,
  },

  // --- Kampanya raporu ---
  report: {
    title: 'Kampanya raporu',
    description:
      'Kaynak ve kampanya başına aday, nitelikli aday, fırsat ve kazanılan satış. Sayılar yalnız görebildiğiniz kayıtlardır; tarih aralığı çalışma alanı saat dilimindedir.',
    from: 'Başlangıç',
    to: 'Bitiş',
    model: 'Atıf modeli',
    modelHint: (m: string) =>
      `Adaylar ve fırsatlar ${m.toLocaleLowerCase('tr-TR')} kaynağına göre gruplanır.`,
    certainty: 'Kesinlik',
    certaintyAll: 'Bütün seviyeler',
    apply: 'Uygula',
    tableTitle: 'Kaynak ve kampanya sonuçları',
    source: 'Kaynak / kampanya',
    leads: 'Aday',
    qualified: 'Nitelikli',
    opportunities: 'Fırsat',
    won: 'Kazanılan',
    revenue: 'Ciro',
    // Oranlar pay / payda tanımıyla (2. tur § 4; `CAMPAIGN_REPORT_RATE_DEFINITIONS`): kümenin ne olduğu başlıkta açık. Raporda
    // "adaydan fırsata" oranı YOK: fırsat sütunu fırsatın kendi temasıyla gruplanır (adaydan dönüşen sayısı değildir).
    rateTitle: {
      qualifiedRate: 'Nitelik oranı',
      opportunityToWon: 'Fırsattan satışa',
    },
    rateHeader: (name: string, numeratorLabel: string, denominatorLabel: string) =>
      `${name} (${numeratorLabel} / ${denominatorLabel})`,
    opportunityGrouping: 'Fırsatın kendi temasına göre',
    rateHint: (name: string, a: number, b: number, p: string) =>
      b === 0
        ? `${name}: — (hesaplanacak kayıt yok)`
        : `${name}: ${a.toLocaleString('tr-TR')} / ${b.toLocaleString('tr-TR')} · ${p}`,
    total: 'Toplam',
    noCampaign: 'Kampanya bilinmiyor',
    notCertain: 'Kampanya kesin değil',
    empty: 'Bu aralıkta aday yok.',
    // Pazarlama çalışma merkezi (24 Ekim 2026): okunur kaynak adı, bağlantılı sayılar, mobil süzgeç çekmecesi ve kartlar
    sourceLookupFailed:
      'Kaynak adları yüklenemedi: sayılar doğru, ancak kaynak adları gösterilemiyor. Sayfayı yenileyin.',
    sourceNameMissing: 'Kaynak adı alınamadı',
    sourceLoading: 'Kaynak adı yükleniyor',
    linkNote:
      'Aday ve nitelikli sayıları aynı dönem, kaynak ve kampanyanın aday listesini açar (liste süzgeci görünür ve temizlenebilir). Fırsat ve kazanılan sayıları fırsat kaydının temasına göredir; listeye açılmaz.',
    certaintyNoLink:
      'Kesinlik süzgeci seçiliyken kaynak ve toplam sayıları aday listesine açılmaz; kampanya satırları açılır.',
    campaignAllLeads: 'kampanyanın tüm adayları',
    openLeads: (what: string, n: string) => `${what}: ${n} — aday listesini aç`,
    openList: 'Aday listesini aç',
    filtersOpen: 'Süzgeçler',
    filtersTitle: 'Rapor süzgeçleri',
    filtersSummary: (from: string, to: string, model: string) => `${from} – ${to} · ${model}`,
    groupTotal: 'Kaynak toplamı',
    distribution: 'Kesinlik dağılımı',
    spendMissing:
      'Harcama verisi bağlı değil: maliyet, reklam getirisi (ROAS) ve aday başına maliyet (CAC) hesaplanmaz.',
    spendCoverage: (pct: string) => `Harcama verisi adayların %${pct} kadarını kapsıyor.`,
    // Reklam hesabı bağlı ama bu dönemin kampanyalarında eşleşen harcama satırı yok: "bağlı" ile "%0" çelişkisi açıkça söylenir
    spendNoMatch:
      'Reklam hesabı bağlı, ancak bu dönemin kampanyalarıyla eşleşen harcama verisi yok: maliyet, reklam getirisi (ROAS) ve aday başına maliyet (CAC) hesaplanmaz.',
    revenueNote:
      'Ciro para birimi başına ayrı gösterilir; kur kaynağı olmadan tek toplamda birleştirilmez. Reklam platformunun bildirdiği dönüşümler bu raporda yoktur — buradaki satışlar CRM kayıtlarıdır.',
    range: 'Aralık geçersiz (en çok 400 gün).',
  },
};
